import type { NavIdProps } from '@vkontakte/vkui';
import { useEffect } from 'react';
import { cabinetApi, formatDateTime, type MyDecision } from '../../api/cabinet';
import { usePagedLoader } from '../../api/paging';
import { MODERATION_TARGET_LABELS } from '../../api/types';
import { countersChanged } from '../../app/counters';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import {
  CardList,
  EmptyState,
  ErrorState,
  Gutter,
  LoadingState,
  Muted,
  Screen,
  Section,
} from '../../shared/ui';
import { VersionDetails } from '../property/versionDetails';
import { ShowMore, personName } from '../review/staffKit';
import { DecisionCard } from './DecisionCard';

/**
 * Что решили по моим заявкам.
 *
 * ТОЛЬКО решения модерации. Архива тут нет намеренно: убрать вещь может и сам
 * владелец, решением модерации это не является, а лежит убранное там же, где
 * и живое, — на вкладке «архив» у склада, питомцев и способностей. Раньше на
 * этот экран приезжала вторая секция с тремя лишними запросами, и вердикты
 * модерации делили его пополам с тем, что модерация не решала.
 */
export function DecisionsPanel({ id }: NavIdProps) {
  const back = useBack();

  const feed = usePagedLoader<MyDecision>(
    (params, signal) => cabinetApi.myDecisions(params, signal),
    [],
  );

  useEffect(() => {
    // Отметка о прочтении — украшение бейджа, а не часть загрузки экрана:
    // не прошла — молчим, иначе игрок получит ошибку там, где он просто читал.
    void cabinetApi
      .markDecisionsRead()
      .then(() => countersChanged())
      .catch(() => undefined);
  }, []);

  const decisions = feed.data?.items ?? [];
  const empty = feed.data !== null && decisions.length === 0;

  return (
    <Screen
      id={id}
      title={t('common.section.decisions')}
      back={back}
      count={feed.data?.total}
      unit="decision"
    >
      {feed.loading && feed.data === null ? <LoadingState /> : null}

      {feed.error !== null && feed.data === null ? (
        <ErrorState title={t('decision.feed.error')} message={feed.error} onRetry={feed.reload} />
      ) : null}

      {empty ? (
        <EmptyState title={t('decision.feed.empty.title')}>
          {t('decision.feed.empty.text')}
        </EmptyState>
      ) : null}

      {decisions.length > 0 ? (
        <Section>
          <Gutter>
            {/* Подписи раздела тут нет: её слово в слово повторяет заголовок
                экрана, а лента на экране одна. */}
            <Muted>{t('decision.feed.hint')}</Muted>
          </Gutter>
          <CardList>
            {decisions.map((decision) => (
              <MyDecisionCard key={decision.id} decision={decision} />
            ))}
          </CardList>
          <ShowMore page={feed.data} onMore={feed.loadMore} loading={feed.loading} />
        </Section>
      ) : null}
    </Screen>
  );
}

function MyDecisionCard({ decision }: { decision: MyDecision }) {
  const subject = decision.subject?.trim() ?? '';
  const target = MODERATION_TARGET_LABELS[decision.target];

  return (
    <DecisionCard
      approved={decision.verdict === 'approve'}
      title={subject === '' ? target : subject}
      // Вид вещи подписью только тогда, когда её название уже стало заголовком.
      target={subject === '' ? null : target}
      date={formatDateTime(decision.decidedAt)}
      comment={decision.comment}
      moderator={personName(decision.moderator)}
      details={<VersionDetails target={decision.target} versionId={decision.targetId} />}
    />
  );
}
